import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LineChart } from 'lucide-react'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts'

type TooltipPayloadEntry = { dataKey?: string | number; value?: number }
type ChartTooltipProps = {
  active?: boolean
  payload?: TooltipPayloadEntry[]
  label?: string
}

function formatCurrencyShort(cents: number): string {
  const dollars = cents / 100
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}k`
  return `$${dollars.toFixed(0)}`
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

function ChartTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length) return null
  const revenue = payload.find((p) => p.dataKey === 'revenueCents')?.value ?? 0
  const cost = payload.find((p) => p.dataKey === 'costCents')?.value ?? 0
  const net = revenue - cost
  return (
    <div className="rounded-md border border-neutral-200 bg-white px-3 py-2 shadow-sm text-xs">
      <div className="font-medium text-neutral-900 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
        <span className="text-neutral-600">Revenue</span>
        <span className="ml-auto font-medium text-emerald-600">{formatCurrency(revenue)}</span>
      </div>
      <div className="flex items-center gap-2 mt-0.5">
        <span className="inline-block h-2 w-2 rounded-full bg-rose-500" />
        <span className="text-neutral-600">Costs</span>
        <span className="ml-auto font-medium text-rose-600">{formatCurrency(cost)}</span>
      </div>
      <div className="flex items-center gap-2 mt-1 pt-1 border-t border-neutral-200">
        <span className="text-neutral-600">Net</span>
        <span className={`ml-auto font-medium ${net >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
          {formatCurrency(net)}
        </span>
      </div>
    </div>
  )
}

export function RevenueVsCostsChart() {
  const data = useQuery(api.costTracking.getCostTimeSeries)
  const series = data?.series ?? []

  const chartData = series.map((p) => ({
    label: new Date(p.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    revenueCents: p.revenueCents,
    costCents: p.costCents,
  }))

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <LineChart className="h-4 w-4" />
          Revenue vs. Costs
        </CardTitle>
        <CardDescription>Cumulative month-to-date</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[280px]">
          {data === undefined ? (
            <div className="h-full flex items-center justify-center text-sm text-neutral-400">
              Loading...
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-neutral-400">
              No data yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="costFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#f43f5e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: '#94a3b8', fontSize: 11 }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                  minTickGap={24}
                />
                <YAxis
                  tick={{ fill: '#94a3b8', fontSize: 11 }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                  tickFormatter={formatCurrencyShort}
                  width={52}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  iconType="circle"
                  wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                  formatter={(value) => (
                    <span className="text-neutral-600">{value === 'revenueCents' ? 'Revenue' : 'Costs'}</span>
                  )}
                />
                <Area
                  type="monotone"
                  dataKey="costCents"
                  stroke="#f43f5e"
                  strokeWidth={2}
                  fill="url(#costFill)"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Area
                  type="monotone"
                  dataKey="revenueCents"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#revenueFill)"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
